import { useCallback } from 'react';
import { useErrorNotification } from '@providers/ErrorsNotificationProvider';
import { useQueryClient } from '@tanstack/react-query';
import { notFound, useNavigate } from '@tanstack/react-router';

import { useIsFeatureEnabled } from '@chessopen/play/featureToggle';
import { useIsAdmin } from '@features/auth/hooks/useIsAdmin';
import { useUserSocket } from '@features/shared/api';
import { UpdateTournamentRequest } from '@features/shared/api/typings';
import {
  updateTournamentCardQuery,
  useAdminDeleteTournamentMutation,
  useCopyTournamentMutation,
  useDeleteTournamentMutation,
  useTournamentCardQuery,
  useTournamentUploadLogoMutation,
  useTournamentUploadRulesMutation,
  useTournamentUploadSponsorLogoMutation,
  useUpdateTournamentMutation,
} from '@features/tournament/api/hooks';
import { useTournamentStates } from '@features/tournament/hooks/useTournamentStates';
import { TournamentEditPageProps } from '@features/tournament/pages/TournamentEditPage/index';

/*
    ____,-------------------------------,____
    \   |             Hook              |   /
    /___|-------------------------------|___\
*/

type PageTab = 'settings' | 'participants';

type TournamentEditParams = {
  clubId: string;
  tournamentId: string;
  pageTab: PageTab;
  onPageTabChange: (newTab: PageTab) => void;
  noCopy?: boolean;
};

export const useTournamentEdit = ({
  clubId,
  tournamentId,
  pageTab,
  onPageTabChange,
  noCopy,
}: TournamentEditParams): (TournamentEditPageProps & { refetchTournament: () => void }) | null => {
  const { data: tournament, refetch, failureReason } = useTournamentCardQuery(clubId, tournamentId);
  if (failureReason && 'httpCode' in failureReason && failureReason.httpCode === 404) {
    throw notFound();
  }

  const isAdmin = useIsAdmin();
  const isVideoAvailable = useIsFeatureEnabled('tournamentVideo', clubId);

  // Отслеживание изменений турнира
  const ql = useQueryClient();
  useUserSocket((e) => {
    switch (e.eventType) {
      case 'TournamentStartsSoon':
      case 'TournamentFinished':
        if (e.tournamentId === tournamentId) {
          updateTournamentCardQuery(ql, clubId, tournamentId, e.tournament);
        }
        break;
    }
  });

  const { isEditable } = useTournamentStates(tournament ?? null);

  // Админ сайта может удалить турнир, когда обычное удаление уже недоступно
  const canForceDelete = isAdmin && !isEditable;

  const {
    onSave,
    onDelete,
    isDeleting,
    onUploadLogo,
    onUploadRules,
    onUploadSponsorLogo,
    onBack,
    onCopy,
  } = useActions(clubId, tournamentId, refetch, canForceDelete);

  if (!tournament) return null;

  const disabled = !isEditable || isDeleting || !tournament;

  return {
    tournament,
    disabled,
    canForceDelete,
    isDeleting,
    onBack,
    onDelete,
    onSave,
    onCopy: noCopy ? undefined : onCopy,
    pageTab,
    onPageTabChange,
    refetchTournament: refetch,
    onUploadRules,
    onUploadLogo,
    onUploadSponsorLogo,
    isAdmin,
    isVideoAvailable,
  };
};

/*
    ____,-------------------------------,____
    \   |           Запчасти            |   /
    /___|-------------------------------|___\
*/

function useActions(
  clubId: string,
  tournamentId: string,
  refetchTournament: () => void,
  canForceDelete: boolean,
) {
  const navigate = useNavigate();
  const { report } = useErrorNotification();

  // Save

  const { mutateAsync: save } = useUpdateTournamentMutation();

  const onSave = useCallback(
    async (tournament: UpdateTournamentRequest, { noRedirect }: { noRedirect?: boolean } = {}) => {
      const result = await save({
        clubId,
        tournament: { ...tournament, tournamentId },
      });

      if (result) {
        if (noRedirect) return;

        // HACK: setTimeout нужен для того, чтобы форма успела стать
        // isSubmitSuccessful и отключила предупреждение о несохранённых данных
        // при попытке покинуть страницу
        setTimeout(() => {
          navigate({
            to: `/club/$clubId/tournament/$tournamentId`,
            params: { clubId, tournamentId },
            from: '/',
          });
        }, 100);
      } else {
        report();
      }
    },
    [save, clubId, tournamentId, navigate, report],
  );

  // Delete

  const { mutateAsync: del, isPending: isDeleting } = useDeleteTournamentMutation();
  const { mutateAsync: adminDel, isPending: isAdminDeleting } = useAdminDeleteTournamentMutation();

  const onDelete = useCallback(async () => {
    const result = canForceDelete
      ? await adminDel(tournamentId)
      : await del({ clubId, tournamentId });
    if (result) {
      navigate({ to: '/club/$clubId', params: { clubId }, from: '/' });
    } else {
      report();
    }
  }, [canForceDelete, adminDel, del, clubId, tournamentId, report, navigate]);

  const { mutateAsync: copyTournament } = useCopyTournamentMutation(clubId, tournamentId);

  const onCopy = useCallback(async () => {
    const result = await copyTournament();
    if (!result?.tournamentId) return report();

    navigate({
      to: '/club/$clubId/tournament/$tournamentId/edit',
      params: { clubId, tournamentId: result.tournamentId },
      search: { noCopy: true },
      from: '/',
    });
  }, [copyTournament, report, clubId, navigate]);

  // Upload rules

  const { mutateAsync: uploadRules } = useTournamentUploadRulesMutation(clubId, tournamentId);

  const onUploadRules = useCallback(
    async (file: FormData) => {
      await uploadRules(file);
      refetchTournament();
    },
    [uploadRules, refetchTournament],
  );

  // Upload logo

  const { mutateAsync: uploadLogo } = useTournamentUploadLogoMutation(clubId, tournamentId);

  const onUploadLogo = useCallback(
    async (file: FormData) => {
      await uploadLogo(file);
      refetchTournament();
    },
    [uploadLogo, refetchTournament],
  );

  // Upload Sponsor Logo

  const { mutateAsync: uploadSponsorLogo } = useTournamentUploadSponsorLogoMutation(
    clubId,
    tournamentId,
  );

  const onUploadSponsorLogo = useCallback(
    async (sponsorNumber: number, formData: FormData) => {
      await uploadSponsorLogo({ sponsorNumber, formData });
      refetchTournament();
    },
    [uploadSponsorLogo, refetchTournament],
  );

  // Exit

  const onBack = useCallback(() => {
    navigate({
      to: '/club/$clubId/tournament/$tournamentId',
      params: { clubId, tournamentId },
      from: '/',
    });
  }, [navigate, clubId, tournamentId]);

  // ...

  return {
    onSave,
    onDelete,
    isDeleting: isDeleting || isAdminDeleting,
    onUploadLogo,
    onUploadRules,
    onUploadSponsorLogo,
    onBack,
    onCopy,
  };
}
